import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Share, Instagram } from 'lucide-react';
import { isInBetaEnvironment } from '../config/features';

const APP_VERSION = 'v1.9.0';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const Footer = ({ setShowPrivacyPolicy }) => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  // false at first render to match prerendered HTML (react-snap is never
  // standalone); the effect below corrects it right after hydration
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setInstalled(true);
      }
      setInstallPrompt(null);
      return;
    }
    setShowGuide((prev) => !prev);
  };

  return (
    <footer className="bg-surface-card border-t border-border relative">
      {/* Install guidance tooltip */}
      {showGuide && (
        <div className="absolute bottom-full right-2 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto mb-2 bg-surface-elevated text-ink text-xs rounded-lg px-4 py-3 shadow-lg max-w-[280px] text-center z-50 border border-border">
          {isIOS() ? (
            <>
              <p className="mb-1 font-medium">Install on iOS</p>
              <p className="text-ink-secondary">
                Tap the <Share className="inline w-3.5 h-3.5 -mt-0.5" /> share button in Safari, then choose <span className="font-medium">"Add to Home Screen"</span>.
              </p>
            </>
          ) : (
            <>
              <p className="mb-1 font-medium">Install this app</p>
              <p className="text-ink-secondary">
                In <span className="font-medium">Chrome</span> or <span className="font-medium">Edge</span>, open the browser menu and choose <span className="font-medium">"Install CONFORM.TOOLS DataCalc"</span>. On <span className="font-medium">Safari (Mac)</span>, use <span className="font-medium">File &rarr; Add to Dock</span>.
              </p>
            </>
          )}
          <button
            onClick={() => setShowGuide(false)}
            className="mt-2 text-conform-red hover:text-conform-yellow text-[10px] uppercase tracking-wider font-bold"
          >
            Got it
          </button>
        </div>
      )}

      {/* Both layouts render; the breakpoint picks one. Choosing in JS made
          the desktop layout appear only after hydration, which shifted the
          whole page (CLS 0.18) because react-snap prerenders at 480px. */}
      <>
        {/* ── Mobile: 3-column layout with safe-area insets ── */}
        <div
          className="flex md:hidden items-center justify-between text-xs text-ink-secondary py-2"
          style={{
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))',
          }}
        >
          {/* Left: brand + version */}
          <div className="flex items-center gap-1.5 min-w-0 shrink">
            <a
              href="https://conform.tools/?utm_source=data-calc&utm_medium=referral&utm_campaign=footer"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center no-underline"
              aria-label="CONFORM.TOOLS — post-production software"
            >
              <img
                src="/images/CT-Wordmark-Red1-sm.png"
                alt="CONFORM.TOOLS"
                width={237}
                height={40}
                className="h-3 w-auto"
              />
            </a>
            <span className="text-ink-tertiary">|</span>
            <span className="text-ink-tertiary whitespace-nowrap">
              {APP_VERSION}{isInBetaEnvironment() && ' BETA'}
            </span>
          </div>

          {/* Center: Feedback */}
          <Link
            to="/request"
            className="text-ink-tertiary hover:text-ink-secondary transition-colors no-underline font-medium"
          >
            Feedback
          </Link>

          {/* Right: icons */}
          <div className="flex items-center gap-2.5 shrink-0">
            {!installed && (
              <button
                onClick={handleInstallClick}
                className="inline-flex items-center text-conform-yellow hover:text-conform-red transition-colors"
                title="Install App"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
            )}
            <a
              href="https://www.instagram.com/conform.tools"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-ink-tertiary hover:text-ink-secondary transition-colors no-underline"
              aria-label="CONFORM.TOOLS on Instagram"
            >
              <Instagram className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={() => setShowPrivacyPolicy(true)}
              className="text-ink-tertiary hover:text-ink-secondary transition-colors"
            >
              Privacy
            </button>
          </div>
        </div>
        {/* ── Desktop: original layout ── */}
        <div className="hidden md:flex items-center justify-between px-5 py-2.5 text-xs text-ink-secondary">
          {/* Left: brand + version */}
          <div className="flex items-center gap-2">
            <a
              href="https://conform.tools/?utm_source=data-calc&utm_medium=referral&utm_campaign=footer"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 no-underline"
            >
              <img
                src="/images/CT-Wordmark-Red1-sm.png"
                alt="CONFORM.TOOLS"
                width={237}
                height={40}
                className="h-3.5 w-auto"
              />
              <span className="text-ink-tertiary hover:text-ink-secondary transition-colors">
                Post-production tools by CONFORM.TOOLS
              </span>
            </a>
            <span className="text-ink-tertiary">|</span>
            <span className="text-ink-tertiary">
              {APP_VERSION}{isInBetaEnvironment() && ' BETA'}
            </span>
          </div>

          {/* Right: links */}
          <div className="flex items-center gap-3">
            {!installed && (
              <button
                onClick={handleInstallClick}
                className="inline-flex items-center gap-1 text-conform-yellow hover:text-conform-red transition-colors font-medium"
                title="Install App"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Install App</span>
              </button>
            )}
            <a
              href="https://www.instagram.com/conform.tools"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-ink-tertiary hover:text-ink-secondary transition-colors no-underline"
              aria-label="CONFORM.TOOLS on Instagram"
            >
              <Instagram className="w-3.5 h-3.5" />
              <span>Instagram</span>
            </a>
            <Link
              to="/request"
              className="text-ink-tertiary hover:text-ink-secondary transition-colors no-underline"
            >
              Feedback
            </Link>
            <button
              onClick={() => setShowPrivacyPolicy(true)}
              className="text-ink-tertiary hover:text-ink-secondary transition-colors"
            >
              Privacy
            </button>
          </div>
        </div>
      </>
    </footer>
  );
};

export default Footer;
